// component/CustomChatMessage.jsx
// --------------------------------
// Renders a single chat message bubble with:
//   • Beautiful Inter-font UI with shadows and clean spacing
//   • Typewriter animation for brand-new bot messages (isNew=true)
//   • Collapsible ReasoningPanel when DEV_MODE reasoning steps are present
//   • Image, markdown, text, error, and selectionlist content types
//   • Copy-to-clipboard button on hover

import React, { useState, useEffect, useRef } from 'react';
import { FaCopy, FaCheck, FaSearch } from 'react-icons/fa';
import TypingIndicator from './TypingIndicator';
import RenderComponents from './RenderComponents';
import { viewBlocksAsText } from './ChatTable';

// ─── Typewriter hook ──────────────────────────────────────────────────────────
// Animates 5 characters per 14ms tick (~350 chars/sec).
// The ref prevents re-triggering on component re-renders.

const useTypewriter = (text, enabled) => {
    const [displayed, setDisplayed] = useState(enabled ? '' : text);
    const [done, setDone] = useState(!enabled);
    const animated = useRef(false);

    useEffect(() => {
        if (!enabled || animated.current || !text) {
            if (!enabled) { setDisplayed(text); setDone(true); }
            return;
        }
        animated.current = true;
        setDisplayed('');
        setDone(false);
        // How much text is shown comes from the CLOCK, not from counting ticks. A long answer
        // is heavy to redraw, and while the person scrolls the browser delays ticks; counting
        // ticks made the typing crawl or stall then. Reading the clock means a late tick just
        // shows more text, so the answer fills at the same pace whether or not they scroll.
        const CHUNK = 5;
        const SPEED = 14;
        // Only the words are typed out: a chart / table block under them (```idoks-view, JSON) appears whole once the
        // words are done - typed, its JSON took many seconds and drew nothing until complete.
        const blockAt = text.indexOf('```idoks-view');
        const end = blockAt >= 0 ? blockAt : text.length;
        const startedAt = Date.now();
        const iv = setInterval(() => {
            const i = Math.floor((Date.now() - startedAt) / SPEED) * CHUNK;
            setDisplayed(text.slice(0, Math.min(i, end)));
            if (i >= end) {
                setDisplayed(text);
                setDone(true);
                clearInterval(iv);
            }
        }, SPEED);
        return () => { clearInterval(iv); animated.current = false; };
    }, []); // intentionally empty — only runs once on mount

    return { displayed, done };
};

// ─── Reasoning panel ─────────────────────────────────────────────────────────

const STEP_CONFIG = {
    user:           { icon: '👤', color: '#6366f1', bg: '#eef2ff' },
    tool_call:      { icon: '🔧', color: '#f59e0b', bg: '#fffbeb' },
    tool_output:    { icon: '📦', color: '#10b981', bg: '#ecfdf5' },
    answer:         { icon: '💬', color: '#3b82f6', bg: '#eff6ff' },
    memory_save:    { icon: '💾', color: '#8b5cf6', bg: '#f5f3ff' },
    memory_get:     { icon: '🧠', color: '#0891b2', bg: '#ecfeff' },
    memory_context: { icon: '📋', color: '#64748b', bg: '#f8fafc' },
};

const ReasoningPanel = ({ steps }) => {
    const [open, setOpen] = useState(false);
    const toolCalls   = steps.filter(s => s.type === 'tool_call').length;
    const memorySteps = steps.filter(s => s.type === 'memory_save' || s.type === 'memory_get').length;
    const parts = [];
    if (toolCalls > 0)   parts.push(`${toolCalls} tool${toolCalls > 1 ? 's' : ''}`);
    if (memorySteps > 0) parts.push(`${memorySteps} memory op${memorySteps > 1 ? 's' : ''}`);
    const summary = parts.length > 0 ? parts.join(' · ') : `${steps.length} step${steps.length > 1 ? 's' : ''}`;

    return (
        <div style={{
            marginBottom: 8,
            border: '1px solid #e5e7eb',
            borderRadius: 10,
            overflow: 'hidden',
            fontSize: 12,
            background: '#fafafa',
        }}>
            <button
                onClick={() => setOpen(o => !o)}
                style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 7,
                    padding: '7px 12px', background: 'transparent', border: 'none',
                    cursor: 'pointer', textAlign: 'left',
                }}
            >
                <span style={{ fontSize: 14 }}>🤔</span>
                <span style={{ fontWeight: 600, color: '#4b5563', flex: 1 }}>
                    Thinking…{' '}
                    <span style={{ fontWeight: 400, color: '#9ca3af' }}>({summary})</span>
                </span>
                <span style={{ color: '#9ca3af', fontSize: 10 }}>{open ? '▲' : '▼'}</span>
            </button>

            {open && (
                <div style={{ borderTop: '1px solid #e5e7eb' }}>
                    {steps.map((step, idx) => {
                        const cfg = STEP_CONFIG[step.type] || STEP_CONFIG.answer;
                        return (
                            <div key={idx} style={{
                                display: 'flex', gap: 10, padding: '8px 12px',
                                borderBottom: idx < steps.length - 1 ? '1px solid #f3f4f6' : 'none',
                                background: idx % 2 === 0 ? '#fff' : '#fafafa',
                            }}>
                                <div style={{
                                    width: 26, height: 26, borderRadius: 6,
                                    background: cfg.bg, display: 'flex',
                                    alignItems: 'center', justifyContent: 'center',
                                    fontSize: 13, flexShrink: 0, marginTop: 1,
                                }}>
                                    {cfg.icon}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{
                                        fontWeight: 600, fontSize: 11, color: cfg.color,
                                        marginBottom: 2, textTransform: 'uppercase',
                                        letterSpacing: '0.04em',
                                    }}>
                                        {step.label}
                                    </div>
                                    <div style={{
                                        color: '#374151', fontSize: 12, lineHeight: 1.5,
                                        wordBreak: 'break-word',
                                        whiteSpace: step.type === 'tool_call' ? 'pre-wrap' : 'normal',
                                        fontFamily: step.type === 'tool_call' ? 'monospace' : 'inherit',
                                        background: step.type === 'tool_call' ? '#f1f5f9' : 'transparent',
                                        padding: step.type === 'tool_call' ? '6px 8px' : 0,
                                        borderRadius: 4,
                                        maxHeight: step.type === 'tool_call' ? 160 : 'none',
                                        overflowY: step.type === 'tool_call' ? 'auto' : 'visible',
                                    }}>
                                        {step.content.length > 1500
                                            ? step.content.slice(0, 1500) + '…'
                                            : step.content}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// ─── "Search Process" panel ──────────────────────────────────────────────────
// Plain-English recap of why the bot searched the way it did, shown to every
// end user (not gated behind DEV_MODE like ReasoningPanel) — closed by default
// so the chat stays clean, but answers "why did it take extra steps?" on demand.

const SearchProcessPanel = ({ text, onViewFull }) => {
    const [open, setOpen] = useState(false);

    return (
        <div style={{
            marginBottom: 6,
            border: '1px solid #e5e7eb',
            borderRadius: 10,
            overflow: 'hidden',
            fontSize: 12,
            background: '#fafafa',
            maxWidth: 420,
        }}>
            <button
                onClick={() => setOpen(o => !o)}
                style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 7,
                    padding: '6px 12px', background: 'transparent', border: 'none',
                    cursor: 'pointer', textAlign: 'left',
                }}
            >
                <FaSearch size={11} color="#6b7280" />
                <span style={{ fontWeight: 600, color: '#4b5563', flex: 1 }}>
                    Answer Trail
                </span>
                <span style={{ color: '#9ca3af', fontSize: 10 }}>{open ? '▲' : '▼'}</span>
            </button>

            {open && (
                <div style={{
                    borderTop: '1px solid #e5e7eb', padding: '8px 12px',
                    color: '#374151', fontSize: 12, lineHeight: 1.6,
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}>
                    {text}
                    {onViewFull && (
                        <div style={{ marginTop: 8, textAlign: 'right' }}>
                            <button
                                onClick={onViewFull}
                                style={{
                                    background: 'none', border: 'none', padding: 0,
                                    color: '#2563eb', fontSize: 11.5, fontWeight: 600,
                                    cursor: 'pointer', textDecoration: 'underline',
                                }}
                            >
                                View full details ↗
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ─── Bot avatar ───────────────────────────────────────────────────────────────

const Avatar = ({ botImage, bottheme, Icon }) => (
    botImage ? (
        /* Wrap logo in a primary-color circle — matches the header avatar appearance */
        <div className={`${bottheme} caw-avatar`} style={{
            width: 32, height: 32, borderRadius: '50%',
            flexShrink: 0, overflow: 'hidden',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '2px solid rgba(255,255,255,0.4)',
        }}>
            <img src={botImage} alt="Bot" style={{
                width: '100%', height: '100%', objectFit: 'cover',
            }} />
        </div>
    ) : (
        <div className={`${bottheme} caw-avatar`} style={{
            width: 32, height: 32, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 15, flexShrink: 0,
        }}>
            {Icon ? <Icon /> : '🤖'}
        </div>
    )
);

// ─── Message footer helpers ──────────────────────────────────────────────────
// The line under every message: when it was sent / received (to the second), how long the answer took,
// and a copy button.

const formatClock = (iso, timezone) => {
    if (!iso) return '';
    const opts = { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true };
    try {
        return new Intl.DateTimeFormat('en-US', { ...opts, timeZone: timezone || 'Asia/Kolkata' }).format(new Date(iso));
    } catch {
        return new Intl.DateTimeFormat('en-US', { ...opts, timeZone: 'Asia/Kolkata' }).format(new Date(iso));
    }
};

const formatFull = (iso, timezone) => {
    if (!iso) return '';
    const opts = { dateStyle: 'medium', timeStyle: 'medium' };
    try {
        return new Intl.DateTimeFormat('en-US', { ...opts, timeZone: timezone || 'Asia/Kolkata' }).format(new Date(iso));
    } catch {
        return new Intl.DateTimeFormat('en-US', { ...opts, timeZone: 'Asia/Kolkata' }).format(new Date(iso));
    }
};

const formatTook = (ms) => {
    if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return '';
    if (ms < 60000) return `${(ms / 1000).toFixed(1)} s`;
    const total = Math.round(ms / 1000);
    return `${Math.floor(total / 60)} m ${total % 60} s`;
};

const MessageFooter = ({ label, iso, timezone, tookMs, canCopy, copied, onCopy, alignRight }) => (
    <div className="caw-time caw-msg-meta" style={{
        fontSize: 11, color: '#9ca3af', marginTop: 3,
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        justifyContent: alignRight ? 'flex-end' : 'flex-start',
    }}>
        <span title={formatFull(iso, timezone)}>
            {label} {formatClock(iso, timezone)}
            {formatTook(tookMs) && <> · took {formatTook(tookMs)}</>}
        </span>
        {canCopy && (
            <button type="button" onClick={onCopy} title={copied ? 'Copied' : 'Copy message'} aria-label="Copy message"
                className="caw-meta-copy" style={{
                    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11,
                    color: copied ? '#10b981' : '#9ca3af',
                }}>
                {copied ? <FaCheck size={10} /> : <FaCopy size={10} />} {copied ? 'Copied' : 'Copy'}
            </button>
        )}
    </div>
);

// ─── Timestamp helper ─────────────────────────────────────────────────────────
// Formats an ISO timestamp as "H:MM AM/PM" in the given IANA timezone.
// Falls back to 'Asia/Kolkata' when timezone is empty or invalid.

// eslint-disable-next-line no-unused-vars -- kept; the message footer above now shows the time to the second
const formatTime = (iso, timezone) => {
    if (!iso) return '';
    const tz = timezone || 'Asia/Kolkata';
    try {
        return new Intl.DateTimeFormat('en-US', {
            hour: 'numeric', minute: 'numeric', hour12: true,
            timeZone: tz,
        }).format(new Date(iso));
    } catch {
        // Invalid timezone string — fall back to Asia/Kolkata
        return new Intl.DateTimeFormat('en-US', {
            hour: 'numeric', minute: 'numeric', hour12: true,
            timeZone: 'Asia/Kolkata',
        }).format(new Date(iso));
    }
};

// ─── Animated bot message content ────────────────────────────────────────────

const TypewriterContent = ({ message, bottheme, onSend, onRetry, onTick }) => {
    // Interactive card kinds (object payloads) — skip typewriter entirely
    const NON_ANIMATED_KINDS = ['visit_choice', 'visit_form', 'selectionlist', 'error', 'error_retry'];
    const kind = message.content?.kind ?? '';
    const isInteractiveCard = NON_ANIMATED_KINDS.includes(kind);

    const rawText = typeof message.content?.payload === 'string' ? message.content.payload : '';

    // Skip typewriter when:
    //   • reasoning steps are present (reasoning panel already gives "live" feel)
    //   • the message is an interactive card with an object payload
    const hasReasoning = Array.isArray(message.reasoning) && message.reasoning.length > 0;
    const shouldAnimate = message.isNew === true && !hasReasoning && !isInteractiveCard;
    const { displayed, done } = useTypewriter(rawText, shouldAnimate);

    // Tell the page each time more text has appeared, so it can keep the newest text in
    // view while the answer fills in (a long answer grows below the visible area).
    useEffect(() => {
        if (shouldAnimate && onTick) onTick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [displayed]);

    // For animated text messages, swap in the partial payload; for cards pass through as-is
    const animatedContent = (!isInteractiveCard && typeof message.content?.payload === 'string')
        ? { ...message.content, payload: displayed }
        : message.content;

    return (
        <>
            <RenderComponents
                message={{ ...message, content: animatedContent }}
                bottheme={bottheme}
                onSend={onSend}
                onRetry={onRetry}
            />
            {!done && (
                <span style={{
                    display: 'inline-block', width: 2, height: '1em',
                    background: '#6b7280', marginLeft: 2, verticalAlign: 'text-bottom',
                    animation: 'ca-cursor-blink 0.7s steps(1) infinite',
                }} />
            )}
        </>
    );
};

// ─── Suggestion Chips ─────────────────────────────────────────────────────────
// Rendered below bot bubbles when the LLM appends a SUGGESTIONS:[...] block.
// Clicking a chip fires onSend(suggestion) — same as typing the message.
// Chips are hidden once any chip has been clicked (one-shot interaction).

const SuggestionChips = ({ suggestions, onSend, bottheme }) => {
    const [used, setUsed] = useState(false);

    if (!suggestions || suggestions.length === 0 || used) return null;

    const handleClick = (text) => {
        setUsed(true);
        onSend(text);
    };

    return (
        <div className="caw-chips" style={{
            marginTop: 10,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 7,
            paddingLeft: 2,
        }}>
            {suggestions.map((s, i) => (
                <button
                    key={i}
                    onClick={() => handleClick(s)}
                    className="caw-chip"
                    style={{
                        padding: '5px 13px',
                        borderRadius: 20,
                        border: '1.5px solid #d1d5db',
                        background: '#fff',
                        color: '#374151',
                        fontSize: 12.5,
                        fontWeight: 500,
                        cursor: 'pointer',
                        lineHeight: 1.4,
                        transition: 'all 0.15s ease',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                        whiteSpace: 'nowrap',
                    }}
                    onMouseEnter={e => {
                        e.currentTarget.style.borderColor = '#6366f1';
                        e.currentTarget.style.color = '#6366f1';
                        e.currentTarget.style.background = '#f5f3ff';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(99,102,241,0.15)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={e => {
                        e.currentTarget.style.borderColor = '#d1d5db';
                        e.currentTarget.style.color = '#374151';
                        e.currentTarget.style.background = '#fff';
                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
                        e.currentTarget.style.transform = 'translateY(0)';
                    }}
                >
                    <span style={{ fontSize: 11, opacity: 0.6 }}>↗</span>
                    {s}
                </button>
            ))}
        </div>
    );
};


// ─── Main component ───────────────────────────────────────────────────────────

// While the Document Finder works: the step running now (and what it is doing), and the steps finished so
// far with their one-line results - all from GET /chat/progress, nothing named here.
const ProgressLines = ({ progress }) => {
    const running = progress?.running || [];
    const done = progress?.done || [];
    if (!running.length && !done.length) return null;
    // `line` is the backend's plain sentence for the step (built from that question's own data; machine text such
    // as JSON stays in the Answer Trail). Older backends send no `line`: the title and result are shown as before.
    const doneText = (s) => s.line || `${s.title}${s.result ? ` → ${s.result}` : ''}`;
    const runText = (r) => r.line || `${r.title}${r.detail ? ` – ${r.detail}` : ''}`;
    return (
        <div className="caw-progress" style={{ marginTop: 6, fontSize: 12, lineHeight: 1.5, color: '#6b7280' }}>
            {progress?.path && (
                <div style={{ marginBottom: 2, fontSize: 11.5, color: '#6b7280' }}>
                    Path: <span style={{ fontWeight: 600, color: '#374151' }}>{progress.path}</span>
                </div>
            )}
            {progress?.headline && (
                <div style={{ color: '#111827', fontWeight: 600, fontSize: 12.5, marginBottom: 2, wordBreak: 'break-word' }}>
                    {progress.headline}…
                </div>
            )}
            {done.slice(-6).map((s, i) => (
                <div key={`d${i}`} style={{ wordBreak: 'break-word' }}>
                    <span style={{ color: '#10b981' }}>✓</span> {doneText(s)}
                </div>
            ))}
            {running.slice(progress?.headline ? 1 : 0).map((r, i) => (
                <div key={`r${i}`} style={{ color: '#374151', fontWeight: 500, wordBreak: 'break-word' }}>
                    {runText(r)}…
                </div>
            ))}
            {progress?.seconds ? (
                <div style={{ marginTop: 2 }}>
                    {progress.seconds < 60
                        ? `${progress.seconds}s`
                        : `${Math.floor(progress.seconds / 60)}m ${progress.seconds % 60}s`}
                </div>
            ) : null}
        </div>
    );
};

const CustomChatMessage = ({ message, bottheme, botImage, onSend, onRetry, timezone, onViewFullTrace, onTypewriterTick, tookMs, progress }) => {
    const [copied, setCopied] = useState(false);

    // Typing indicator pseudo-message
    if (message.id === 'typing-indicator') {
        return (
            <div className="caw-msg caw-msg-bot" style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginBottom: 16 }}>
                {/* Icon was missing here - every other bot message passes it (below), so
                    this was the one place that always fell back to the generic 🤖 emoji
                    instead of matching the icon shown everywhere else. */}
                <Avatar bottheme={bottheme} botImage={botImage} Icon={message.avatarIconComponent} />
                <div className="caw-bubble caw-bubble-bot caw-bubble-typing" style={{
                    background: '#fff', border: '1px solid #e5e7eb',
                    borderRadius: '4px 18px 18px 18px',
                    padding: '10px 14px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                }}>
                    <TypingIndicator />
                    <ProgressLines progress={progress} />
                </div>
            </div>
        );
    }

    const isUser = message.sender === 'user';
    const rawText = message.content?.payload ?? (typeof message.content === 'string' ? message.content : '');
    const isInIframe = window.self !== window.top;
    // Only text can be copied (a form or a card has no text to copy).
    const canCopy = typeof rawText === 'string' && rawText.trim() !== '';

    const handleCopy = async () => {
        try {
            // A chart / table block is copied as a plain table, not as its JSON.
            const copyText = viewBlocksAsText(rawText);
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(copyText);
            } else {
                // Fallback for iframes / non-secure contexts — works without clipboard-write permission
                const ta = document.createElement('textarea');
                ta.value = copyText;
                ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none;';
                document.body.appendChild(ta);
                ta.focus();
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
            }
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch { }
    };

    // ── User message ───────────────────────────────────────────────────────────
    if (isUser) {
        return (
            <div className="caw-msg caw-msg-user" style={{
                display: 'flex', justifyContent: 'flex-end',
                marginBottom: 14, paddingLeft: '20%',
            }}>
                <div>
                    <div className="msg-group" style={{ position: 'relative', display: 'inline-block' }}>
                        <div className={`${bottheme} caw-bubble caw-bubble-user`} style={{
                            color: '#fff', padding: '10px 14px',
                            borderRadius: '18px 18px 4px 18px',
                            fontSize: 14, lineHeight: 1.6, wordBreak: 'break-word',
                            // The person's own spacing and new lines, as typed (plain text is otherwise
                            // collapsed onto one line).
                            whiteSpace: 'pre-wrap',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.14)',
                        }}>
                            {rawText}
                        </div>
                        {!isInIframe && (
                            <button onClick={handleCopy} title="Copy" className="msg-copy-btn caw-copy" style={{
                                position: 'absolute', top: '50%', transform: 'translateY(-50%)',
                                left: -30, background: '#fff', border: '1px solid #e5e7eb',
                                borderRadius: '50%', width: 24, height: 24,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                            }}>
                                {copied ? <FaCheck size={9} color="#10b981" /> : <FaCopy size={9} color="#9ca3af" />}
                            </button>
                        )}
                    </div>
                    <MessageFooter
                        label="Sent" iso={message.timestamp} timezone={timezone} alignRight
                        canCopy={canCopy && isInIframe} copied={copied} onCopy={handleCopy}
                    />
                </div>
            </div>
        );
    }

    // ── Bot message ────────────────────────────────────────────────────────────
    return (
        <>
            <style>{`
                @keyframes ca-cursor-blink { 0%, 100% { opacity: 1 } 50% { opacity: 0 } }
                .msg-group .msg-copy-btn { opacity: 0; transition: opacity 0.2s; }
                .msg-group:hover .msg-copy-btn { opacity: 1; }
            `}</style>

            <div className="caw-msg caw-msg-bot" style={{
                display: 'flex', alignItems: 'flex-start', gap: 8,
                marginBottom: 16, paddingRight: '10%',
            }}>
                <Avatar
                    bottheme={bottheme}
                    botImage={botImage}
                    Icon={message.avatarIconComponent}
                />

                <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Plain-English recap — shown to every end user, closed by default */}
                    {message.howFoundSummary && (
                        <SearchProcessPanel
                            text={message.howFoundSummary}
                            onViewFull={
                                Array.isArray(message.fullTrace) && message.fullTrace.length > 0 && onViewFullTrace
                                    ? onViewFullTrace
                                    : null
                            }
                        />
                    )}

                    {/* Reasoning panel — only shown when reasoning steps exist (DEV_MODE) */}
                    {Array.isArray(message.reasoning) && message.reasoning.length > 0 && (
                        <ReasoningPanel steps={message.reasoning} />
                    )}

                    {/* Bubble */}
                    <div className="msg-group" style={{ position: 'relative', display: 'inline-block', maxWidth: '100%', minWidth: 0 }}>
                        <div className="caw-bubble caw-bubble-bot" style={{
                            maxWidth: '100%', overflowX: 'clip',
                            background: '#fff', border: '1px solid #e5e7eb',
                            borderRadius: '4px 18px 18px 18px',
                            padding: '10px 14px', fontSize: 14, lineHeight: 1.6,
                            wordBreak: 'break-word', color: '#1f2937',
                            boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
                        }}>
                            <TypewriterContent message={message} bottheme={bottheme} onSend={onSend} onRetry={onRetry} onTick={onTypewriterTick} />
                        </div>
                        {!isInIframe && (
                            <button onClick={handleCopy} title="Copy" className="msg-copy-btn caw-copy" style={{
                                position: 'absolute', top: '50%', transform: 'translateY(-50%)',
                                right: -30, background: '#fff', border: '1px solid #e5e7eb',
                                borderRadius: '50%', width: 24, height: 24,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                            }}>
                                {copied ? <FaCheck size={9} color="#10b981" /> : <FaCopy size={9} color="#9ca3af" />}
                            </button>
                        )}
                    </div>

                    <MessageFooter
                        label="Received" iso={message.receivedAt || message.timestamp} timezone={timezone} tookMs={tookMs}
                        canCopy={canCopy && isInIframe} copied={copied} onCopy={handleCopy}
                    />

                    {/* Suggestion chips — only on the final/complete bubble */}
                    <SuggestionChips
                        suggestions={message.suggestions}
                        onSend={onSend}
                        bottheme={bottheme}
                    />
                </div>
            </div>
        </>
    );
};

export default CustomChatMessage;
